import { HttpAction } from "@megaron/http-service";
import { Failure, Ok } from "@megaron/result";
import {
  Serializer,
  serializerExtensions,
  SerializerExtensions,
  Serializers,
  SerializerValue,
} from "@megaron/serializers";

export const unitTemplateCategories = [
  "custom",
  "pallet",
  "alias",
  "package",
  "layer",
  "box",
  "extraLayer",
  "extendedPallet",
  "mix",
  "optional",
  "test",
] as const;

export type UnitTemplateCategory = (typeof unitTemplateCategories)[number];

export type UnitTemplateComponent = {
  unit: string;
  quantity: number;
};

export type UnitTemplateComponents = Record<string, number>;

export const unitTemplateComponentSerializer: Serializer<UnitTemplateComponent> &
  SerializerExtensions<UnitTemplateComponent> = {
  schema: { kind: "unitTemplateComponent", version: 1 },
  serialize: ({ unit, quantity }) => {
    if (typeof unit !== "string" || !Number.isInteger(quantity)) throw new Error("InvalidUnitTemplateComponent");
    return `${unit} × ${quantity}`;
  },
  deserialize: (raw) => {
    if (typeof raw !== "string") return Failure("InvalidUnitTemplateComponent");

    const separator = " × ";
    const separatorIndex = raw.lastIndexOf(separator);
    if (separatorIndex === -1) return Failure("InvalidUnitTemplateComponent");

    const unit = raw.slice(0, separatorIndex);
    const rawQuantity = raw.slice(separatorIndex + separator.length);
    if (!/^-?\d+$/.test(rawQuantity)) return Failure("InvalidUnitTemplateComponent");

    const quantity = Number(rawQuantity);
    if (!Number.isSafeInteger(quantity)) return Failure("InvalidUnitTemplateComponent");

    return Ok({ unit, quantity });
  },
  ...serializerExtensions(),
};

export const unitTemplateComponentsSerializer: Serializer<UnitTemplateComponents> &
  SerializerExtensions<UnitTemplateComponents> = {
  schema: { kind: "unitTemplateComponents", version: 1 },
  serialize: (components) =>
    Object.entries(components)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([unit, quantity]) => unitTemplateComponentSerializer.serialize({ unit, quantity }))
      .join("\n"),
  deserialize: (raw) => {
    if (typeof raw !== "string") return Failure("InvalidUnitTemplateComponents");
    if (raw === "") return Ok({});

    const components: UnitTemplateComponents = {};
    for (const [index, line] of raw.split(/\r?\n/).entries()) {
      const result = unitTemplateComponentSerializer.deserialize(line);
      if (result.isFailure) return Failure({ [index]: result.error });
      if (result.value.unit in components) return Failure({ [index]: "DuplicateUnitTemplateComponent" });
      components[result.value.unit] = result.value.quantity;
    }

    return Ok(components);
  },
  ...serializerExtensions(),
};

export const unitTemplateSerializer = Serializers.object({
  id: Serializers.string,
  unit: Serializers.string,
  category: Serializers.stringOneOf(...unitTemplateCategories),
  label: Serializers.string,
  complexity: Serializers.integer,
  isMandatory: Serializers.boolean,
  components: unitTemplateComponentsSerializer,
});

export type UnitTemplate = SerializerValue<typeof unitTemplateSerializer>;

export const unitTemplateActions = {
  unitTemplateQuery: HttpAction({
    path: "/unitTemplates",
    method: "get",
    qsSerializer: Serializers.object({
      searchText: Serializers.string,
      id: Serializers.string.array().asJsonString(),
      unit: Serializers.string.array().asJsonString(),
      descendantOf: Serializers.string,
      ancestorOf: Serializers.string,
      category: Serializers.stringOneOf(...unitTemplateCategories)
        .array()
        .asJsonString(),
      component: Serializers.string,
    }).partial(),
    requiresAuth: true as const,
    valueSerializer: Serializers.object({
      units: unitTemplateSerializer.array(),
      count: Serializers.integer,
    }),
  }),

  saveUnitTemplate: HttpAction({
    path: "/unitTemplates",
    method: "put",
    requiresAuth: true as const,
    bodySerializer: unitTemplateSerializer,
    valueSerializer: unitTemplateSerializer,
    errorSerializer: Serializers.stringOneOf("NoComponents", "InvalidQuantity", "CyclicDependency"),
  }),

  deleteUnitTemplate: HttpAction({
    path: "/unitTemplates/:id",
    method: "delete",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({ id: Serializers.string }),
    valueSerializer: unitTemplateSerializer,
    errorSerializer: Serializers.stringOneOf("NotFound"),
  }),
};
